import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRequestById, updateRequest, removeRequest } from "../utils/api";

export default function EditRequest({ token }) {
  const nav = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [tags, setTags] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr("");
      setLoading(true);

      try {
        const data = await getRequestById(token, id);
        const r = data?.request || data;

        if (cancelled) return;

        setTitle(r?.title || "");
        setDescription(r?.description || "");
        setBudget(String(r?.max_budget ?? ""));

        const t = Array.isArray(r?.tags) ? r.tags : [];
        setTags(t.join(", "));
      } catch (e) {
        if (!cancelled) setErr("Failed to load request.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, token]);

  async function submit(e) {
    e.preventDefault();
    setErr("");

    if (!token) {
      setErr("Please log in first.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title,
        description,
        max_budget: budget ? Number(budget) : null,
      };

      await updateRequest(token, id, payload);
      nav(`/requests/${id}`);
    } catch (e2) {
      setErr("Failed to update request.");
    } finally {
      setSaving(false);
    }
  }

  async function del() {
    if (!token) {
      setErr("Please log in first.");
      return;
    }
    const ok = window.confirm("Delete this request?");
    if (!ok) return;

    setSaving(true);
    setErr("");
    try {
      await removeRequest(token, id);
      nav("/requests");
    } catch (e) {
      setErr("Failed to delete request.");
    } finally {
      setSaving(false);
    }
  }

  if (!token) {
    return (
      <div className="container pagePadBottom">
        <div className="card">
          <div style={{ fontWeight: 700 }}>Login required</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            You need to log in to edit a request.
          </div>
          <div style={{ marginTop: 12 }}>
            <Link className="btnPrimary" to="/login">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container pagePadBottom">
      <div className="pageHeader">
        <div className="pageHeaderLeft">
          <div className="h2">Edit Request</div>
          <div className="muted small" style={{ marginTop: 6 }}>
            Update details or remove it.
          </div>
        </div>
        <div className="pageHeaderRight">
          <Link className="btnSecondary" to={`/requests/${id}`}>
            Back
          </Link>
        </div>
      </div>

      {err ? (
        <div className="alert alertDanger">
          <div className="alertTitle">Error</div>
          <div className="alertBody">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="emptyBox">
          <div className="emptyTitle">Loading…</div>
          <div className="muted small">Fetching request.</div>
        </div>
      ) : (
        <form onSubmit={submit} className="card" style={{ marginTop: 14 }}>
          <div className="label">Title</div>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="divider" />

          <div className="formRow">
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="label">Budget</div>
              <input
                className="input"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              />
            </div>
            <div style={{ flex: 2, minWidth: 240 }}>
              <div className="label">Tags (comma separated)</div>
              <input
                className="input"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
            </div>
          </div>

          <div className="divider" />

          <div className="label">Description</div>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            style={{ resize: "vertical" }}
          />

          <div className="divider" />

          <div
            className="row"
            style={{
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <button className="btnPrimary" type="submit" disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>

            <button
              className="btnDanger"
              type="button"
              onClick={del}
              disabled={saving}
            >
              Delete
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
